import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Shield, Clock, CheckCircle, XCircle,
  AlertCircle, Play, Eye, ChevronLeft,
  Search, Download, TrendingUp,
  Users, BookOpen, Award
} from 'lucide-react';
import { courseAssignmentAnalyticsService } from '@/services/courseAssignmentAnalytics.service';
import { userService } from '@/services/user.service';
import type { UserAnalytics, RoleAnalytics } from '@/types/courseAssignmentAnalytics.types';
import { toast } from '@/components/ui/Toast';
import { Select } from '@/components/ui/Select';
import { useForm } from 'react-hook-form';
import { useDebounce, DEFAULT_DEBOUNCE_DELAY } from '@/utils/debounce';

interface RoleInfo {
  _id: string;
  role_key: string;
  role_name: string;
  description?: string;
  permissions?: string[];
  created_at?: string;
  updated_at?: string;
}

interface FilterFormValues {
  courseStatus: string;
  certificationStatus: string;
  sortBy: string;
}

export default function RoleCourseList() {
  const { roleKey } = useParams<{ roleKey: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [users, setUsers] = useState<UserAnalytics[]>([]);
  const [roleInfo, setRoleInfo] = useState<RoleInfo | null>(null);
  const [roleAnalytics, setRoleAnalytics] = useState<RoleAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const searchQuery = useDebounce(searchInput, DEFAULT_DEBOUNCE_DELAY);
  const [courseStatus, setCourseStatus] = useState<string>('all');
  const [certificationStatus, setCertificationStatus] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('progress');
  const [isExporting, setIsExporting] = useState(false);
  const [totalAssignedCourses, setTotalUserAssignments] = useState(0);

  // Form setup for Select components
  const { control, watch } = useForm<FilterFormValues>({
    defaultValues: {
      courseStatus: 'all',
      certificationStatus: 'all',
      sortBy: 'progress'
    }
  });

  // Watch form changes and sync with state
  useEffect(() => {
    const subscription = watch((value) => {
      if (value.courseStatus !== undefined) {
        setCourseStatus(value.courseStatus);
      }
      if (value.certificationStatus !== undefined) {
        setCertificationStatus(value.certificationStatus);
      }
      if (value.sortBy !== undefined) {
        setSortBy(value.sortBy);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  useEffect(() => {
    fetchData();
  }, [roleKey]);

  // Handle dynamic back navigation
 const handleBackNavigation = () => {
      const returnPath = location.state?.from || '/admin/analytics/role-progress';
      navigate(returnPath);
  };

  const fetchData = async () => {
    if (!roleKey) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Fetch role info
      const roleResponse = await userService.getRoleByKey(roleKey);
      if (roleResponse?.data) {
        setRoleInfo(roleResponse.data);
      }

      // Fetch role analytics with role_key filter
      const analyticsResponse = await courseAssignmentAnalyticsService.getRoleAnalytics(1, 1000, '', '', '', roleKey);
      const roleData = analyticsResponse.roles.find(r => r.roleKey === roleKey);
      if (roleData) {
        setRoleAnalytics(roleData);
      }

      // Fetch all users in this role with their course progress (no pagination)
      const userAnalyticsResponse = await courseAssignmentAnalyticsService.getAllUserAnalytics({
        roleKey: roleKey
      });

      if (userAnalyticsResponse?.users) {
        const usersData = userAnalyticsResponse.users;
        setUsers(usersData);

      const totalAssignments = usersData.reduce(
          (sum, user) => sum + (user.assignedCourses || 0),
          0
        );
        setTotalUserAssignments(totalAssignments);
      }
    } catch (err: any) {
      console.error('Error fetching data:', err);
      setError('Failed to load role course data');
      toast.error('Failed to load role course data');
    } finally {
      setLoading(false);
    }
  };

  // Select options
  const courseStatusOptions = [
    { label: 'All Course Status', value: 'all' },
    { label: 'Not Started', value: 'not_started' },
    { label: 'In Progress', value: 'in_progress' },
    { label: 'Completed', value: 'completed' },
  ];

  const certificationStatusOptions = [
    { label: 'All Cert Status', value: 'all' },
    { label: 'Not Started', value: 'not_started' },
    { label: 'In Progress', value: 'in_progress' },
    { label: 'Completed', value: 'completed' },
  ];

  const sortOptions = [
    { label: 'Progress', value: 'progress' },
    { label: 'Course Time Spent', value: 'timeSpent' },
    { label: 'Certification Time Spent', value: 'certificationTimeSpent' },
    { label: 'Certification Count', value: 'certificationCount' },
    { label: 'Completed Courses', value: 'completedCourseCount' },
    { label: 'Last Active', value: 'lastActive' },
    { label: 'Name', value: 'name' }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-green-600 bg-green-100';
      case 'in_progress':
        return 'text-blue-600 bg-blue-100';
      case 'not_started':
        return 'text-gray-600 bg-gray-100';
      case 'overdue':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4" />;
      case 'in_progress':
        return <Play className="w-4 h-4" />;
      case 'not_started':
        return <AlertCircle className="w-4 h-4" />;
      case 'overdue':
        return <XCircle className="w-4 h-4" />;
      default:
        return <AlertCircle className="w-4 h-4" />;
    }
  };

  const formatTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const formatTimeFromSeconds = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getEngagementColor = (engagement: string) => {
    switch (engagement) {
      case 'high':
        return 'text-green-600';
      case 'medium':
        return 'text-yellow-600';
      case 'low':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  // Helper function to format date, handling 'na' and invalid dates
  const formatDate = (dateValue: any): string => {
    if (!dateValue || dateValue === 'na' || dateValue === 'N/A') {
      return '-';
    }
    try {
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) {
        return '-';
      }
      return date.toLocaleDateString();
    } catch (error) {
      return '-';
    }
  };

  // Helper function to calculate user status based on course_stats
  const getUserStatus = (user: UserAnalytics): string => {
    if (!user.course_stats) {
      return user.status || 'not_started';
    }

    const { completed, in_progress, not_started } = user.course_stats;
    const totalCourses = completed + in_progress + not_started;

    // If no courses assigned
    if (totalCourses === 0) {
      return 'not_started';
    }

    // If all courses are completed
    if (completed === totalCourses) {
      return 'completed';
    }

    // If all courses are not started
    if (not_started === totalCourses) {
      return 'not_started';
    }

    // Otherwise, it's in progress (some courses started but not all completed)
    return 'in_progress';
  };

  // Helper function to calculate certification status based on certification_stats
  const getCertificationStatus = (user: UserAnalytics): string => {
    if (!user.certification_stats) {
      return 'not_started';
    }

    const { completed, in_progress, not_started } = user.certification_stats;
    const totalCertifications = completed + in_progress + not_started;

    // If no certifications assigned
    if (totalCertifications === 0) {
      return 'not_started';
    }

    // If all certifications are completed
    if (completed === totalCertifications) {
      return 'completed';
    }

    // If all certifications are not started
    if (not_started === totalCertifications) {
      return 'not_started';
    }

    // Otherwise, it's in progress (some certifications started but not all completed)
    return 'in_progress';
  };

  const filteredUsers = users.filter(user => {
    // Apply course status filter
    if (courseStatus !== 'all' && getUserStatus(user) !== courseStatus) {
      return false;
    }

    // Apply certification status filter
    if (certificationStatus !== 'all' && getCertificationStatus(user) !== certificationStatus) {
      return false;
    }

    // Apply search filter
    if (searchQuery &&
        !user.userName.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !user.email.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }

    return true;
  });

  // Sort users
  const sortedUsers = [...filteredUsers].sort((a, b) => {
    switch (sortBy) {
      case 'progress':
        return b.progress - a.progress;
      case 'timeSpent':
        return b.timeSpent - a.timeSpent;
      case 'certificationTimeSpent':
        return b.certification_time_spent - a.certification_time_spent;
      case 'certificationCount':
        return b.certification_count - a.certification_count;
      case 'completedCourseCount':
        return b.completedCourseCount - a.completedCourseCount;
      case 'lastActive':
        return new Date(b.lastActive || 0).getTime() - new Date(a.lastActive || 0).getTime();
      case 'name':
        return a.userName.localeCompare(b.userName);
      default:
        return 0;
    }
  });

  // Calculate statistics
  const stats = {
    total: users.length,
    // Course stats - total count across all users
    courseCompleted: users.reduce((sum, u) => sum + (u.course_stats?.completed || 0), 0),
    courseInProgress: users.reduce((sum, u) => sum + (u.course_stats?.in_progress || 0), 0),
    courseNotStarted: users.reduce((sum, u) => sum + (u.course_stats?.not_started || 0), 0),
    averageCourseProgress: users.reduce((sum, u) => sum + u.progress, 0) / (users.length || 1),
    // Certification stats - total count across all users
    certCompleted: users.reduce((sum, u) => sum + (u.certification_stats?.completed || 0), 0),
    certInProgress: users.reduce((sum, u) => sum + (u.certification_stats?.in_progress || 0), 0),
    certNotStarted: users.reduce((sum, u) => sum + (u.certification_stats?.not_started || 0), 0),
    averageCertProgress: users.length > 0 ? users.reduce((sum, u) => {
      const totalCerts = u.certification_count || 0;
      const completedCerts = u.certification_stats?.completed || 0;
      return sum + (totalCerts > 0 ? (completedCerts / totalCerts) * 100 : 0);
    }, 0) / users.length : 0,
    // Time stats
    totalTime: users.reduce((sum, u) => sum + u.timeSpent, 0),
    totalCertificationTime: users.reduce((sum, u) => sum + u.certification_time_spent, 0),
    highEngagement: users.filter(u => u.engagement === 'high').length
  };
  // Get engagement level based on average progress
  const getEngagementLevel = (avgProgress: number) => {
    if (avgProgress >= 80) return { label: 'High Engagement', color: 'bg-green-100 text-green-700' };
    if (avgProgress >= 50) return { label: 'Medium Engagement', color: 'bg-yellow-100 text-yellow-700' };
    return { label: 'Low Engagement', color: 'bg-red-100 text-red-700' };
  };

  // Handle export functionality
  const handleExport = async () => {
    setIsExporting(true);
    toast.loading('Preparing export...');

    try {
      // Simulate export preparation time
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Create CSV content
      const headers = ['User Name', 'Email', 'Status', 'Progress (%)', 'Courses Completed/Assigned', 'Time Spent (min)', 'Engagement', 'Last Active'];
      const csvContent = [headers.join(',')];

      sortedUsers.forEach(user => {
        const row = [
          `"${user.userName}"`,
          `"${user.email}"`,
          user.status,
          user.progress,
          `"${user.coursesCompleted}/${user.coursesAssigned}"`,
          user.timeSpent,
          user.engagement,
          formatDate(user.lastActive)
        ];
        csvContent.push(row.join(','));
      });

      // Download CSV file
      const blob = new Blob([csvContent.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `role_${roleKey}_course_progress_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.dismiss();
      toast.success('Export completed successfully!');
    } catch (error) {
      toast.dismiss();
      toast.error('Export failed. Please try again.');
      console.error('Export error:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleViewUserCourses = (userId: string) => {
    // Navigate to user's course list
    navigate(
      `/admin/users/${userId}/courses`,
      { state: { from: `/admin/roles/${roleKey}/courses` } }
    );
  };

  const handleViewUserCertifications = (userId: string) => {
    // Navigate to user's certification list
    navigate(
      `/admin/users/${userId}/certifications`,
      { state: { from: `/admin/roles/${roleKey}/courses` } }
    );
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        {/* Header Skeleton */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-gray-200 rounded-lg"></div>
            <div>
              <div className="h-8 w-64 bg-gray-200 rounded mb-2"></div>
              <div className="h-4 w-48 bg-gray-200 rounded"></div>
            </div>
          </div>
          <div className="h-10 w-24 bg-gray-200 rounded-lg"></div>
        </div>

        {/* Role Info Card Skeleton */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-gray-200 rounded-full"></div>
            <div className="flex-1">
              <div className="h-6 w-48 bg-gray-200 rounded mb-2"></div>
              <div className="h-4 w-96 bg-gray-200 rounded mb-3"></div>
              <div className="flex items-center gap-4">
                <div className="h-4 w-24 bg-gray-200 rounded"></div>
                <div className="h-4 w-24 bg-gray-200 rounded"></div>
                <div className="h-4 w-32 bg-gray-200 rounded"></div>
                <div className="h-6 w-32 bg-gray-200 rounded-full"></div>
              </div>
            </div>
          </div>
        </div>

        {/* Statistics Cards Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white p-6 rounded-xl border border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <div className="w-8 h-8 bg-gray-200 rounded"></div>
                <div className="h-8 w-16 bg-gray-200 rounded"></div>
              </div>
              <div className="h-4 w-24 bg-gray-200 rounded mb-3"></div>
              <div className="h-3 w-32 bg-gray-200 rounded"></div>
            </div>
          ))}
        </div>

        {/* Filters Skeleton */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[300px] h-10 bg-gray-200 rounded-lg"></div>
            <div className="w-48 h-10 bg-gray-200 rounded-lg"></div>
            <div className="w-48 h-10 bg-gray-200 rounded-lg"></div>
            <div className="w-48 h-10 bg-gray-200 rounded-lg"></div>
          </div>
        </div>

        {/* Legend Skeleton */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4">
          <div className="flex items-center justify-between">
            <div className="h-4 w-32 bg-gray-200 rounded"></div>
            <div className="flex items-center gap-6">
              <div className="h-4 w-24 bg-gray-200 rounded"></div>
              <div className="h-4 w-24 bg-gray-200 rounded"></div>
              <div className="h-4 w-24 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>

        {/* Table Skeleton */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((i) => (
                    <th key={i} className="px-6 py-3">
                      <div className="h-4 bg-gray-200 rounded"></div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {[1, 2, 3, 4, 5].map((row) => (
                  <tr key={row}>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((col) => (
                      <td key={col} className="px-6 py-4">
                        <div className="h-4 bg-gray-200 rounded"></div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <p className="text-gray-900 font-medium mb-1">Failed to Load</p>
          <p className="text-gray-600">{error}</p>
          <button
            onClick={fetchData}
            className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={handleBackNavigation}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Go back"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Role Overview</h1>
            <p className="text-gray-600 mt-1">
              Assignment data of {roleInfo?.role_name || roleKey}
            </p>
          </div>
        </div>
        {/* <div className="flex gap-3">
          <button
            onClick={handleExport}
            disabled={isExporting || users.length === 0}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className={`w-4 h-4 ${isExporting ? 'animate-pulse' : ''}`} />
            {isExporting ? 'Exporting...' : 'Export'}
          </button>
        </div> */}
      </div>

      {/* Role Info Card */}
      {roleInfo && (
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-gradient-to-br from-brand-purple-400 to-brand-purple-600 rounded-full flex items-center justify-center text-white">
              <Shield className="w-8 h-8" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-gray-900">
                {roleInfo.role_name}
              </h2>
              {roleInfo.description && (
                <p className="text-gray-600">{roleInfo.description}</p>
              )}
              <div className="flex items-center gap-4 mt-2 text-sm">
                <span className="flex items-center gap-1">
                  <Users className="w-4 h-4 text-gray-400" />
                  {roleAnalytics?.totalUsers || users.length} Users
                </span>
                <span className="flex items-center gap-1">
                  <BookOpen className="w-4 h-4 text-gray-400" />
                 {roleAnalytics?.assignedCourses} Courses
                </span>
                <span className="flex items-center gap-1">
                  <CheckCircle className="w-4 h-4 text-gray-400" />
                  {roleAnalytics?.certification_count || 0} Certifications
                </span>
                {roleAnalytics && (
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    getEngagementLevel(roleAnalytics?.avgProgress || stats.averageProgress).color
                  }`}>
                    {getEngagementLevel(roleAnalytics?.avgProgress || stats.averageProgress).label}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Users */}
        <div className="bg-white p-6 rounded-xl border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <Users className="w-8 h-8 text-brand-blue-600" />
            <span className="text-2xl font-bold text-gray-900">{stats.total}</span>
          </div>
          <p className="text-gray-600 text-sm">Total Users</p>
          <div className="mt-3 text-xs text-gray-500">
            {stats.highEngagement} high engagement
          </div>
        </div>

        {/* Average Course Progress */}
        <div className="bg-white p-6 rounded-xl border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <BookOpen className="w-8 h-8 text-brand-blue-600" />
            <span className="text-2xl font-bold text-gray-900">
              {Math.round(stats.averageCourseProgress)}%
            </span>
          </div>
          <p className="text-gray-600 text-sm">Avg Course Progress</p>
          <div className="mt-3">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-brand-blue-600 h-2 rounded-full"
                style={{ width: `${stats.averageCourseProgress}%` }}
              />
            </div>
          </div>
        </div>

        {/* Average Cert Progress */}
        <div className="bg-white p-6 rounded-xl border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <Award className="w-8 h-8 text-brand-purple-600" />
            <span className="text-2xl font-bold text-gray-900">
              {Math.round(stats.averageCertProgress)}%
            </span>
          </div>
          <p className="text-gray-600 text-sm">Avg Cert Progress</p>
          <div className="mt-3">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-brand-purple-600 h-2 rounded-full"
                style={{ width: `${stats.averageCertProgress}%` }}
              />
            </div>
          </div>
        </div>

        {/* Total Time Invested */}
        <div className="bg-white p-6 rounded-xl border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <Clock className="w-8 h-8 text-brand-orange-600" />
            <span className="text-2xl font-bold text-gray-900">
              {formatTimeFromSeconds(stats.totalTime + stats.totalCertificationTime)}
            </span>
          </div>
          <p className="text-gray-600 text-sm">Total Time</p>
          <div className="mt-3 text-xs text-gray-500">
            <div>Course: {formatTimeFromSeconds(stats.totalTime)}</div>
            <div>Cert: {formatTimeFromSeconds(stats.totalCertificationTime)}</div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-[300px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full pl-10 pr-4 py-2 min-h-[42px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="w-48">
            <Select
              name="courseStatus"
              control={control}
              options={courseStatusOptions}
              isSearchable={false}
              placeholder="Course status"
            />
          </div>

          <div className="w-48">
            <Select
              name="certificationStatus"
              control={control}
              options={certificationStatusOptions}
              isSearchable={false}
              placeholder="Cert status"
            />
          </div>

          <div className="w-48">
            <Select
              name="sortBy"
              control={control}
              options={sortOptions}
              isSearchable={false}
              placeholder="Sort by"
            />
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-700">Status Legend</h3>
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span className="text-sm text-gray-600"><span className="font-medium text-green-600">C</span> - Completed</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-500"></div>
              <span className="text-sm text-gray-600"><span className="font-medium text-blue-600">P</span> - In Progress</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-gray-500"></div>
              <span className="text-sm text-gray-600"><span className="font-medium text-gray-600">N</span> - Not Started</span>
            </div>
          </div>
        </div>
      </div>

      {/* User List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Course Status
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Progress
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Courses
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Time Spent (Course)
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cert Status
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Certifications
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Time Spent (Cert)
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Engagement
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Active
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {sortedUsers.map((user) => (
                <tr key={user.userId} className="hover:bg-gray-50">
                  {/* User */}
                  <td className="px-6 py-4">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {user.userName}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {user.email}
                      </p>
                    </div>
                  </td>

                  {/* Course Status */}
                  <td className="px-6 py-4">
                    {user.course_stats ? (
                      <div className="flex items-center justify-center">
                        <div className="flex flex-col text-center">
                          <div className="flex gap-1 text-xs">
                            <span className="text-green-600 font-medium">{user.course_stats.completed}</span>
                            <span className="text-gray-400">/</span>
                            <span className="text-blue-600 font-medium">{user.course_stats.in_progress}</span>
                            <span className="text-gray-400">/</span>
                            <span className="text-gray-600 font-medium">{user.course_stats.not_started}</span>
                          </div>
                          <span className="text-xs text-gray-500">C/P/N</span>
                        </div>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">-</span>
                    )}
                  </td>

                  {/* Progress */}
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-center gap-3">
                      <div className="flex-1 max-w-[100px]">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${
                              user.progress >= 80 ? 'bg-green-600' :
                              user.progress >= 50 ? 'bg-blue-600' :
                              user.progress >= 20 ? 'bg-yellow-600' :
                              'bg-red-600'
                            }`}
                            style={{ width: `${user.progress}%` }}
                          />
                        </div>
                      </div>
                      <span className="text-sm font-medium text-gray-700">
                        {user.progress}%
                      </span>
                    </div>
                  </td>

                  {/* Courses */}
                  <td className="px-6 py-4 text-center text-sm text-gray-900">
                    {user.assignedCourses}
                  </td>

                  {/* Time Spent (Course) */}
                  <td className="px-6 py-4 text-center text-sm text-gray-600">
                    {formatTimeFromSeconds(user.timeSpent)}
                  </td>

                  {/* Cert Status */}
                  <td className="px-6 py-4">
                    {user.certification_stats ? (
                      <div className="flex items-center justify-center">
                        <div className="flex flex-col text-center">
                          <div className="flex gap-1 text-xs">
                            <span className="text-green-600 font-medium">{user.certification_stats.completed}</span>
                            <span className="text-gray-400">/</span>
                            <span className="text-blue-600 font-medium">{user.certification_stats.in_progress}</span>
                            <span className="text-gray-400">/</span>
                            <span className="text-gray-600 font-medium">{user.certification_stats.not_started}</span>
                          </div>
                          <span className="text-xs text-gray-500">C/P/N</span>
                        </div>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">-</span>
                    )}
                  </td>

                  {/* Certifications */}
                  <td className="px-6 py-4 text-center text-sm text-gray-900">
                    {user.certification_count}
                  </td>

                  {/* Time Spent (Cert) */}
                  <td className="px-6 py-4 text-center text-sm text-gray-600">
                    {formatTimeFromSeconds(user.certification_time_spent)}
                  </td>

                  {/* Engagement */}
                  <td className="px-6 py-4 text-center">
                    <span className={`text-sm font-medium ${getEngagementColor(user.engagement)}`}>
                      {user.engagement}
                    </span>
                  </td>

                  {/* Last Active */}
                  <td className="px-6 py-4 text-center text-sm text-gray-600">
                    {formatDate(user.lastActive)}
                  </td>

                  {/* Actions */}
                  <td className="px-6 py-4 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => handleViewUserCourses(user.userId)}
                        className="p-2 text-brand-blue-600 hover:bg-brand-blue-50 rounded-lg transition-colors"
                        title="View Courses"
                      >
                        <BookOpen className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleViewUserCertifications(user.userId)}
                        className="p-2 text-brand-purple-600 hover:bg-brand-purple-50 rounded-lg transition-colors"
                        title="View Certifications"
                      >
                        <Award className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {sortedUsers.length === 0 && (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No Tracks found in this role</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}